import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Users, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useUI } from "@/components/Layout";
import { Button } from "@/components/ui/button";

export default function Friends() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { openChat } = useUI();

  const { data: requests = [] } = useQuery({
    queryKey: ["friend-requests"],
    queryFn: async () => (await api.get("/friends/requests")).data,
  });
  const { data: friends = [] } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await api.get("/friends")).data,
  });

  const respond = async (id, action) => {
    await api.post(`/friends/respond/${id}?action=${action}`);
    toast.success(action === "accept" ? "Friend added" : "Request removed");
    qc.invalidateQueries({ queryKey: ["friend-requests"] });
    qc.invalidateQueries({ queryKey: ["friends"] });
    qc.invalidateQueries({ queryKey: ["suggestions"] });
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-5">
        <h2 className="font-heading text-xl font-extrabold flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-[#C084FC]" />Friend Requests</h2>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending requests.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.request_id} data-testid={`request-${r.request_id}`} className="flex items-center gap-3">
                <Avatar user={r.user} size={48} onClick={() => navigate(`/profile/${r.user.user_id}`)} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1">{r.user.name}{r.user.verified && <VerifiedBadge size={13} />}</div>
                </div>
                <Button data-testid={`accept-${r.request_id}`} onClick={() => respond(r.request_id, "accept")} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white h-9 gap-1"><Check className="w-4 h-4" />Accept</Button>
                <Button data-testid={`reject-${r.request_id}`} onClick={() => respond(r.request_id, "reject")} variant="outline" className="rounded-xl border-border h-9"><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5">
        <h2 className="font-heading text-xl font-extrabold mb-4">All Friends ({friends.length})</h2>
        {friends.length === 0 ? (
          <p className="text-sm text-muted-foreground">You have no friends yet. Find people to connect with!</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {friends.map((u) => (
              <div key={u.user_id} data-testid={`friend-${u.user_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-[#6C0BA9]/40 transition-all">
                <Avatar user={u} size={48} onClick={() => navigate(`/profile/${u.user_id}`)} />
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/profile/${u.user_id}`)} className="font-semibold text-sm flex items-center gap-1 hover:underline">{u.name}{u.verified && <VerifiedBadge size={13} />}</button>
                </div>
                <button data-testid={`message-friend-${u.user_id}`} onClick={() => openChat(u)} className="w-9 h-9 rounded-full bg-[#6C0BA9]/15 text-[#C084FC] hover:bg-[#6C0BA9]/30 flex items-center justify-center transition-colors"><MessageCircle className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
